import { BCMSRecord, DisabledPrompt, Link, useGlobalController, useParams, useRoute, useRouteData } from "@bcm-dev/bcms-vite";
import PageView from "compid/shared/containers/PageViewContainer";
import WidgetContainer from "compid/shared/containers/WidgetContainer";
import WidgetHeaderView from "compid/shared/containers/WidgetContainer/views/WidgetHeaderView";
import UIButton from "compid/ui/UIButton";
import { Account, Autopay, Bill, Biller, Payment, Reminder } from "model";
import React from "react";
import { ImageAsset, SVGAsset } from "res";
import BillerDetailsAutopayUpsell from "./components/BillerDetailsAutopayUpsell";
import BillerDetailsDesktopView from "./components/BillerDetailsDesktopView";
import BillerDetailsMobileView from "./components/BillerDetailsMobileView";
import BillerDetailsReminderUpsell from "./components/BillerDetailsReminderUpsell";
import BillerDetailseBillUpsell from "./components/BillerDetailseBillUpsell";
import "./styles.css";
/**
 * @function BillerDetailsPage
 * @return {React.Component}
 * @exported true
 * @description The main component for BillerDetailsPage
 */
export default function BillerDetailsPage(props) { 
  return(
    <PageView className="biller-details-page">
      <BillerDetailsSubHeader/>
      
      <div className="show-on-desktop">
        <BillerDetailsDesktopView />
      </div>
      <div className="show-on-mobile show-on-tablet">
        <BillerDetailsMobileView />
      </div>
    </PageView>
  );
}
/**
 * @function BillerDetailsSubHeader
 * @return {React.Component}
 * @param {STRING} billerLogo - The logo for the selected Biller
 * @param {STRING} billerName - The Biller name
 * @param {STRING} billerNumber - The four digit account number for the Biller
 * @exported true
 * @description This is the component that displays the biller logo, biller name, and the biller account number.
 */
 export function BillerDetailsSubHeader(props) {
  const navigate = useRoute();
  const { route } = useRouteData();

  const goBack = () => navigate(route === null || route.goBack === undefined ? "/PayBills" : -1);

  return(
    <div className="flex-container page-sub-header">
      <p className="flex-width semi-bold-text">BILLER DETAILS</p>
      <span className="flex-container biller-details-close" onClick={goBack}>
        <p className="link">Close</p>
        <SVGAsset id="CloseIcon" className="right-icon link" data-point="Biller_Details_Close_Icon"/>
      </span>
    </div>
  );
}
/**
 * @function BillerLogoView
 * @return {React.Component}
 * @param {STRING} billerLogo - The logo for the selected Biller
 * @param {STRING} billerName - The Biller name
 * @param {STRING} billerNumber - The four digit account number for the Biller
 * @exported true
 * @description This is the component that displays the biller logo, biller name, and the biller account number.
 */
export function BillerLogoView(props) {
  const {biller,bill} = GetBiller();

  const billerLogo = biller.logo ? <ImageAsset id={biller.logo} className="logo-icon" />: <SVGAsset id="MagnifyingGlassIcon" className="magnifying-glass-icon"/>;
  const billerName = biller.name ?? "[billerName]";
  const billerNumber = biller.number ?? "[billerNumber]";
  
  return(
    <div className="biller-logo-view flex-container">
      {billerLogo}
      <div className="biller-info">
        <h3><b>{billerName}</b></h3>
        <p className="gray-text">{ biller.nickname } {billerNumber}</p>
      </div>
    </div>
  );
}

/**
 * @function BillerDetailsWidget
 * @return {React.Component}
 * @param {STRING} billerID - The id of the biller
 * @param {STRING} billerAddress - The Billers address
 * @param {STRING} billerCity - The Billers City
 * @param {STRING} billerState - The Billers State
 * @param {STRING} billerZip - The Billers Zip-code
 * @param {STRING} billerPhone - The Billers phone number
 * @exported true
 * @description This is the component that displays the biller address and phone number. It also contains the pay and edit biller buttons.
 */
export function BillerDetailsWidget(props) {
  const navigate = useRoute();
  const {biller,bill} = GetBiller();
  const billerName = biller.name ?? "[billerName]"
  const billerNumber = biller.number ?? "[billerNumber]";
  const billerAddress = biller.address ?? "[billerAddress]";
  const billerCity = biller.city ?? "[billerCity]";
  const billerState = biller.state ?? "[billerState]";
  const billerZip = biller.zip ?? "[billerZip]";
  const billerPhone = biller.phone ?? "[billerPhone]";

  // Multiple autopays can be set up for a biller.
  // There may be some older code referencing 1 autopay for 1 biller.
  const hasAutopays = Autopay.initAll().filter(item => item.billerID === bill.biller.id).length > 0;
  const showReminders = bill.biller.eBillStatus === "Not Enrolled" && !hasAutopays;
  //const showReminders = bill.biller.eBillStatus === "Not Enrolled" && !(bill.biller.autopay instanceof Autopay);
  const showAutoPromo = !(bill.biller.autopay instanceof Autopay);
  const showActiveEBill = biller.eBillStatus === "Active";

  const billerLocation = `${ billerCity } ${ billerState }, ${ billerZip }`;
  const showBillerLocation = billerLocation.replace(/[, ]/g, "").length > 0;

  const onSubmit = () => navigate(`/addReminderWorkflow/${ biller.id }`);
  const reminders = Reminder.initAll().filter(reminder => {
    if(reminder.id === undefined) { 
      return false;
    }
    return reminder.biller.id === biller.id;
  });

  const reminder = reminders.length > 0 ? reminders[0] : undefined;

  return(
    <div className="biller-details-widget flex-container flex-vertical">
      <div className="flex-container biller-details-container">
        <div className="flex-width">
          <div className="show-on-desktop">
            <BillerLogoView />
          </div>
          <div className="more-biller-info">
            <p className="gray-text">{billerAddress}</p>
            <p className="gray-text">{ showBillerLocation && billerLocation }</p>
            <p className="gray-text">{ billerPhone.length > 0 && `Phone: ${ billerPhone } `}</p>
          </div>
        </div>
        <div className="flex-container flex-vertical set-width button-container">
          <UIButton className="primary-button" onClick={ () => navigate(`/makeAPayment/${ bill.id }`) } dataPoint="Biller_Details_Pay">Pay</UIButton>
          <UIButton className="secondary-button" onClick={ () => navigate(`/editBillerWorkflow/${ biller.id }`) } dataPoint="Biller_Details_Edit_Biller">Edit Biller</UIButton>
        </div>
      </div>

      <BillerDetailseBillUpsell biller={ biller } bill={ bill } />
      <BillerDetailsAutopayUpsell biller={ biller } />
      { showReminders && 
        <BillerDetailsReminderUpsell reminder={ reminder } onSubmit={ onSubmit }/>
      }
    </div>
  );
}

/**
 * @function BillerActivityWidget
 * @return {React.Component}
 * @param {STRING} billerID - The id of the biller
 * @exported true
 * @description This is the component that displays the recent activity for the selected biller.
 */
export function BillerActivityWidget(props) {
  const navigate = useRoute();
  const { biller, bill } = GetBiller();
  const billerPayments = Payment.sortDateAscending(Payment.initAll())
    .filter(payment => payment.to.id === biller.id, []);

    // Append "paid" bills to the end of the activity widget.
    const billItems = Bill.initAll().reduce((acc, bill) => {
      const paidBill = bill.biller.id === biller.id && bill.status.toLowerCase() === "paid" && bill.biller.eBillStatus.toLowerCase() ==="active";
      if(paidBill) {
        acc.push(
          <MarkAsPaidBillerActivityView key={ bill.id } paymentAmount={ bill.amount.format() } bill={ bill } dataPoint={ `Biller_Details_Bill` } />
        );
      }

      return acc;
    }, []);

  const amountClick = paymentID => evt => navigate(`/viewPayment/${paymentID}`);

  const paymentItems = billerPayments.map(payment => {
    const paymentStatus = payment.status.toLowerCase();
    const paymentStatusFormatted = paymentStatus.charAt(0).toUpperCase() + paymentStatus.slice(1);
    
    return (
        <div key={ payment.id }>
          <div className="biller-activity-item flex-container flex-horizontal align-center space-between">
            { paymentStatus !== "processing" &&
              <p className="amount clickable link" onClick={ amountClick(payment.id) } data-point={ `Biller_Details_Payment_Amount_${ payment.id }`}>{ payment.amount.format() }</p>
            }
            { paymentStatus === "processing" &&
              <DisabledPrompt>
                <p className="amount clickable link" data-point={ `Biller_Details_Payment_Amount_${ payment.id }`}>{ payment.amount.format() }</p>
              </DisabledPrompt>
            }
            <div className="biller-activity-right-column flex-container flex-horizontal align-center">
              { payment.hasRelatedAutopay && <SVGAsset id="AutoPayIcon" className="set-width left-icon" /> }
              <p className="sub-text" data-point={ `Biller_Details_Payment_Subtext_${ payment.id }`}>{ paymentStatusFormatted + " " + payment.date.format('M d') }</p>
            </div>
          </div>
        </div>
    );
  }, []);

  return(
    <div className="biller-activity-widget">
      <WidgetContainer className="recent-activity">
        <WidgetHeaderView title="Biller activity">
          <Link to={`/activity/${biller.id}`} dataPoint="Biller_Details_View_All_Activity_Link">View all</Link>
        </WidgetHeaderView>
        { paymentItems }
        { billItems }
      </WidgetContainer>
    </div>
  );
}

/**
 * @function MarkAsPaidBillerActivityView
 * @return {React.Component}
 * @param {STRING} paymentAmount - The amount of the payment
 * @exported false
 * @description This is the component that displays billers that were marked as paid.
 */
function MarkAsPaidBillerActivityView(props) {
  const paymentAmount = props.paymentAmount ?? "[$X.XX]";
  const bill = props.bill ?? {};
  const amountDataPoint = props.dataPoint ? { "data-point" : props.dataPoint + "_Amount_" + bill.id } : {};
  const subtextDataPoint = props.dataPoint ? { "data-point" : props.dataPoint + "_Subtext_" + bill.id } : {};


  return(
        <div>
          <div className="biller-activity-item flex-container flex-horizontal align-center space-between marked-as-paid">
            <p className="amount" { ...amountDataPoint }>{paymentAmount}</p>
            <div className="biller-activity-right-column flex-container flex-horizontal align-center">
              <p className="sub-text" { ...subtextDataPoint }>Marked as paid</p>
            </div>
          </div>
        </div>
  );
}

function GetBiller(){
  // Get the global controller, and the 'id' property if passed
  const controller = useGlobalController();
  const { id } = useParams();
  // Initalize all model types
  BCMSRecord.globalControllerFor([ Biller, Account, Payment, Bill, Autopay, Reminder ], controller);

  // If the 'id' is not undefined, initalize the biller otherwise initalize the first biller
  let biller = id !== undefined ? Biller.initByID(id) : Biller.firstBiller(controller.state);
  biller = biller !== undefined ? biller : Biller.firstBiller(controller.state);
  
  // Get the bill of the biller
  const bill = Bill.getBillByBiller(biller.id);
  return {
    biller:biller,
    bill:bill
    }
} 